import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import Icon from '../Shared/Icons';
import { useAuth } from '../../context/AuthContext';
import { getProgram } from '../../config/programs';
import { SESSION_FIELDS, RATING_LABELS } from '../../utils/reportSchema';
import {
  fetchClubs, fetchMembers, fetchReports, saveSessionReport,
  deleteReport, resolveAuthorName,
} from '../../utils/programData';
import { isoWeekKey, weekLabel, fmtDate, fmtDateTime } from '../../utils/weekUtils';
import { ProgramShell, Field, S, T } from './ui';

const EMPTY = {
  kindOfEvent: 'session',
  date: new Date().toISOString().split('T')[0],
  time: '14:00',
  clubId: '',
  venue: '',
  eventName: '',
};

export default function SessionReport() {
  const { pid } = useParams();
  const [params] = useSearchParams();
  const { currentUser, userRole } = useAuth();
  const program = getProgram(pid);

  const [clubs, setClubs] = useState([]);
  const [members, setMembers] = useState([]);
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState(EMPTY);
  const [values, setValues] = useState({});
  const [attendance, setAttendance] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(params.get('new') === '1');
  const [openId, setOpenId] = useState(params.get('open') || null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { if (program) load(); /* eslint-disable-next-line */ }, [pid]);

  async function load() {
    try {
      const [cs, ms, rs] = await Promise.all([
        fetchClubs(pid), fetchMembers(pid),
        fetchReports({ programId: pid, kind: 'session' }),
      ]);
      setClubs(cs); setMembers(ms); setList(rs);
    } catch (e) { console.error(e); }
  }

  if (!program) return <Navigate to="/programs" />;

  const clubMembers = meta.clubId
    ? members.filter(m => m.clubId === meta.clubId)
    : members;

  function change(k, v) { setValues(prev => ({ ...prev, [k]: v })); }
  function changeMeta(k, v) {
    setMeta(prev => ({ ...prev, [k]: v }));
    if (k === 'clubId') setAttendance({});
  }

  function mark(id, status) {
    setAttendance(a => ({ ...a, [id]: status }));
  }
  function markAll(status) {
    const a = {};
    clubMembers.forEach(m => { a[m.id] = status; });
    setAttendance(a);
  }

  function reset() {
    setMeta(EMPTY); setValues({}); setAttendance({}); setEditingId(null); setShowForm(false);
  }

  function edit(r) {
    setMeta({
      kindOfEvent: r.kindOfEvent || 'session',
      date: r.date || EMPTY.date,
      time: r.time || '',
      clubId: r.clubId || '',
      venue: r.venue || '',
      eventName: r.eventName || '',
    });
    setValues(r.values || {});
    setAttendance(r.attendance || {});
    setEditingId(r.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  }

  async function submit() {
    if (!values.howItWent) { setErr('Eleza session ilikuwaje kwa jumla.'); window.scrollTo(0, 0); return; }
    setSaving(true); setErr('');
    try {
      const name = await resolveAuthorName(currentUser);
      const club = clubs.find(c => c.id === meta.clubId);
      const present = Object.values(attendance).filter(v => v === 'present').length;
      await saveSessionReport({
        programId: pid,
        ...meta,
        clubName: club ? club.name : (meta.eventName || ''),
        weekKey: isoWeekKey(new Date(meta.date)),
        weekLabel: weekLabel(new Date(meta.date)),
        values,
        attendance,
        presentCount: present,
        totalCount: Object.keys(attendance).length,
      }, { uid: currentUser.uid, name }, editingId);
      setMsg(editingId ? 'Ripoti ya session imebadilishwa.' : 'Ripoti ya session imehifadhiwa.');
      reset(); load();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { console.error(e); setErr('Imeshindikana kuhifadhi.'); }
    setSaving(false);
  }

  async function remove(id) {
    if (!window.confirm('Futa ripoti hii ya session?')) return;
    try { await deleteReport(id); load(); } catch (e) { console.error(e); }
  }

  return (
    <ProgramShell program={program} tab="session">

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '10px' }}>
        <div>
          <h2 style={{ ...S.h2, marginBottom: '3px' }}>Ripoti za Session & Matukio</h2>
          <div style={{ fontSize: '12px', color: T.muted }}>
            Ripoti ya jumla ya session nzima — si ya mtu mmoja mmoja
          </div>
        </div>
        <button style={{ ...S.btn, background: program.color, padding: '11px 18px' }}
          onClick={() => (showForm ? reset() : setShowForm(true))}>
          {showForm ? 'Funga' : '+ Andika'}
        </button>
      </div>

      {msg && <div style={S.ok}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}

      {showForm && (
        <>
          <div style={S.card}>
            <label style={S.label}>Aina ya ripoti</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
              {[
                { k: 'session', l: 'Session ya kawaida' },
                { k: 'event', l: 'Tukio / Activity' },
              ].map(o => (
                <button key={o.k} onClick={() => changeMeta('kindOfEvent', o.k)}
                  style={{
                    flex: 1, padding: '11px', borderRadius: '11px', cursor: 'pointer',
                    border: `1px solid ${meta.kindOfEvent === o.k ? program.color : T.line}`,
                    background: meta.kindOfEvent === o.k ? program.color : '#fff',
                    color: meta.kindOfEvent === o.k ? '#fff' : T.muted,
                    fontSize: '12.5px', fontWeight: 700, fontFamily: 'inherit',
                  }}>{o.l}</button>
              ))}
            </div>

            {meta.kindOfEvent === 'event' && (
              <>
                <label style={S.label}>Jina la tukio</label>
                <input style={{ ...S.input, marginBottom: '16px' }} value={meta.eventName}
                  onChange={e => changeMeta('eventName', e.target.value)}
                  placeholder="mf. Mashindano ya chess ya wilaya" />
              </>
            )}

            <label style={S.label}>Club</label>
            <select style={{ ...S.input, marginBottom: '16px' }} value={meta.clubId}
              onChange={e => changeMeta('clubId', e.target.value)}>
              <option value="">— Wote / haihusiani na club moja —</option>
              {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label style={S.label}>Tarehe</label>
                <input type="date" style={{ ...S.input, marginBottom: '16px' }} value={meta.date}
                  onChange={e => changeMeta('date', e.target.value)} />
              </div>
              <div style={{ flex: '1 1 110px' }}>
                <label style={S.label}>Muda</label>
                <input type="time" style={{ ...S.input, marginBottom: '16px' }} value={meta.time}
                  onChange={e => changeMeta('time', e.target.value)} />
              </div>
            </div>

            <label style={S.label}>Mahali</label>
            <input style={{ ...S.input }} value={meta.venue}
              onChange={e => changeMeta('venue', e.target.value)} placeholder="mf. Ukumbi wa shule" />
          </div>

          {/* Mahudhurio */}
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ ...S.h2, marginBottom: 0 }}>Mahudhurio ({clubMembers.length})</h2>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button style={{ ...S.btnGhost, padding: '6px 11px', fontSize: '11px' }} onClick={() => markAll('present')}>Wote wapo</button>
                <button style={{ ...S.btnGhost, padding: '6px 11px', fontSize: '11px' }} onClick={() => setAttendance({})}>Futa</button>
              </div>
            </div>

            {clubMembers.length === 0 ? (
              <div style={S.empty}>Hakuna mwanachama kwenye club hii bado.</div>
            ) : clubMembers.map(m => (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '9px 0', borderTop: `1px solid #F3F1EB`,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: T.ink }}>{m.fullName}</div>
                  <div style={{ fontSize: '10.5px', color: T.muted }}>{m.school || m.centre || m.area || ''}</div>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {[
                    { k: 'present', l: 'Yupo', c: '#059669' },
                    { k: 'late', l: 'Amechelewa', c: '#D97706' },
                    { k: 'absent', l: 'Hayupo', c: '#DC2626' },
                  ].map(o => (
                    <button key={o.k} onClick={() => mark(m.id, attendance[m.id] === o.k ? '' : o.k)}
                      style={{
                        padding: '6px 10px', borderRadius: '8px', cursor: 'pointer',
                        border: `1px solid ${attendance[m.id] === o.k ? o.c : T.line}`,
                        background: attendance[m.id] === o.k ? o.c : '#fff',
                        color: attendance[m.id] === o.k ? '#fff' : T.muted,
                        fontSize: '10.5px', fontWeight: 700, fontFamily: 'inherit',
                      }}>{o.l}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Maswali ya session */}
          <div style={S.card}>
            <h2 style={S.h2}>Session Ilikuwaje?</h2>
            {SESSION_FIELDS.map(f => (
              <Field key={f.key} field={f} value={values[f.key]} onChange={change} color={program.color} />
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '26px' }}>
            <button style={{ ...S.btn, background: program.color, flex: 1, opacity: saving ? 0.6 : 1 }}
              disabled={saving} onClick={submit}>
              {saving ? 'Inahifadhi...' : editingId ? 'Hifadhi mabadiliko' : 'Hifadhi ripoti'}
            </button>
            <button style={S.btnGhost} onClick={reset}>Ghairi</button>
          </div>
        </>
      )}

      {/* Orodha ya ripoti */}
      {!showForm && (
        list.length === 0 ? (
          <div style={{ ...S.card, ...S.empty }}>
            Bado hakuna ripoti ya session kwenye programu hii.
          </div>
        ) : list.map(r => {
          const isOpen = openId === r.id;
          return (
            <div key={r.id} style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
              <div onClick={() => setOpenId(isOpen ? null : r.id)}
                style={{ padding: '15px 18px', cursor: 'pointer', display: 'flex', gap: '11px', alignItems: 'center' }}>
                <div style={{
                  width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0,
                  background: r.kindOfEvent === 'event' ? '#FFF7ED' : `${program.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={r.kindOfEvent === 'event' ? 'bolt' : 'calendar'} size={15}
                    color={r.kindOfEvent === 'event' ? '#E07A2E' : program.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 700, color: T.ink }}>
                    {r.eventName || r.clubName || 'Session'}
                  </div>
                  <div style={{ fontSize: '11px', color: T.muted, marginTop: '3px' }}>
                    {r.date || fmtDate(r.createdAt)} · {r.authorName} · {r.presentCount ?? 0}/{r.totalCount ?? 0} walihudhuria
                  </div>
                </div>
                <span style={{ color: T.muted, transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
              </div>

              {isOpen && (
                <div style={{ padding: '0 18px 18px', borderTop: `1px solid ${T.line}` }}>
                  <div style={{ paddingTop: '14px' }}>
                    {SESSION_FIELDS.filter(f => r.values?.[f.key]).map(f => (
                      <div key={f.key} style={{ marginBottom: '13px' }}>
                        <div style={{ fontSize: '11.5px', color: T.muted, fontWeight: 700 }}>{f.label}</div>
                        <div style={{ fontSize: '13.5px', color: T.ink, marginTop: '3px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {f.type === 'rating' ? RATING_LABELS[r.values[f.key]] : r.values[f.key]}
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize: '11px', color: T.muted, marginTop: '14px' }}>
                      Iliandikwa {fmtDateTime(r.createdAt)}
                      {r.lastEditedByName && r.lastEditedByName !== r.authorName ? ` · ilihaririwa na ${r.lastEditedByName}` : ''}
                    </div>
                    {(userRole === 'admin' || r.authorId === currentUser?.uid) && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '13px' }}>
                        <button style={{ ...S.btnGhost, padding: '7px 13px', fontSize: '11.5px' }} onClick={() => edit(r)}>Badilisha</button>
                        <button style={{ ...S.btnGhost, padding: '7px 13px', fontSize: '11.5px', color: '#DC2626' }} onClick={() => remove(r.id)}>Futa</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

    </ProgramShell>
  );
}
